/**
 * @description Client-side port of operation_hub prescription governance QC
 * (verifyPrescriptionUnits / calculatePrescriptionUnitsHelper / buildPrescriptionCheckPayload).
 * Message strings must stay identical to lib/diagnosis/index.js in operation_hub.
 * The backend remains the final gate on /prescription/add and /prescription/edit.
 */

const MASS_UNITS = ['mg', 'mcg', 'µg', 'ug', 'g', 'gram', 'grams', 'milligram', 'milligrams', 'microgram', 'micrograms']

function toNum(v) {
  if (v == null || v === '') return null
  if (typeof v === 'object' && v.value != null) return Number(v.value)
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return ''
  const num = Number(n)
  if (Number.isInteger(num)) return String(num)
  return String(parseFloat(num.toFixed(4)))
}

function formatDoseUnitLabel(doseUnit) {
  const u = String(doseUnit || '').trim().toLowerCase()
  if (!u) return 'Tablet'
  if (u.startsWith('tablete') || u.startsWith('tablet') || u === 'tab' || u === 'tabs') return 'Tablet'
  if (u.startsWith('capsule') || u === 'cap' || u === 'caps') return 'Capsule'
  if (u.includes('syringe')) return 'Syringe'
  if (u === 'ml' || u.startsWith('millil')) return 'ml'
  if (u === 'each') return 'Each'
  const cleaned = String(doseUnit).trim()
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase()
}

function pluralizeDoseUnitLabel(unitLabel) {
  const label = String(unitLabel || '').trim()
  if (!label) return ''
  const lower = label.toLowerCase()
  if (lower === 'each' || lower === 'ml') return label
  return `${label}s`
}

function resolveDispensableUnitLabel(doseUnit, drug) {
  const strengthUnit = drug.STRENGTH_UNIT != null ? String(drug.STRENGTH_UNIT).trim() : ''
  if (strengthUnit) return formatDoseUnitLabel(doseUnit)
  const packageType = drug.PACKAGE_TYPES != null ? String(drug.PACKAGE_TYPES).trim() : ''
  if (packageType) return formatDoseUnitLabel(packageType)
  return formatDoseUnitLabel(doseUnit)
}

function resolveDrugStrengthForDisplay(drug) {
  const strengthUnit = drug.STRENGTH_UNIT != null ? String(drug.STRENGTH_UNIT).trim() : ''
  if (!strengthUnit) return { value: null, unit: null }
  if (drug.STRENGTH_VALUE != null && !Number.isNaN(Number(drug.STRENGTH_VALUE))) {
    return { value: Number(drug.STRENGTH_VALUE), unit: strengthUnit }
  }
  const raw = drug.STRENGTH != null ? String(drug.STRENGTH).trim() : ''
  if (!raw) return { value: null, unit: null }
  const match = raw.match(/^(\d+(\.\d+)?)/)
  if (match) return { value: Number(match[1]), unit: strengthUnit }
  const numOnly = Number(raw)
  if (!Number.isNaN(numOnly)) return { value: numOnly, unit: strengthUnit }
  return { value: null, unit: null }
}

/** Strength used for unit conversion (generic resolveDrugStrength). */
function resolveDrugStrength(drug) {
  if (drug.STRENGTH_VALUE != null && drug.STRENGTH_UNIT) {
    return { value: Number(drug.STRENGTH_VALUE), unit: String(drug.STRENGTH_UNIT).trim() }
  }
  const raw = drug.STRENGTH != null ? String(drug.STRENGTH).trim() : ''
  if (!raw) return { value: null, unit: null }
  const match = raw.match(/^(\d+(\.\d+)?)\s*([a-zA-Zµμ]+)/)
  if (match) return { value: Number(match[1]), unit: match[3] }
  const numOnly = Number(raw)
  if (!Number.isNaN(numOnly)) return { value: numOnly, unit: drug.STRENGTH_UNIT || 'mg' }
  return { value: null, unit: null }
}

function toMg(amount, unit) {
  const value = Number(amount)
  if (Number.isNaN(value)) return null
  const u = String(unit || '').trim().toLowerCase()
  if (u === 'mg' || u === 'milligram' || u === 'milligrams') return value
  if (u === 'g' || u === 'gram' || u === 'grams') return value * 1000
  if (u === 'mcg' || u === 'µg' || u === 'ug' || u === 'microgram' || u === 'micrograms') return value / 1000
  return null
}

function resolveUnitsPerPack(drug) {
  const candidates = [drug.UNITS_PER_PACK, drug.Units_Per_Pack, drug.PACK_SIZE, drug.PACKAGE_SIZE, drug.SIZE, drug.UNITS_PER_BOX]
  for (const c of candidates) {
    const n = Number(c)
    if (c != null && !Number.isNaN(n) && n > 0) return n
  }
  return null
}

/** Daily dispensable units (calculatePrescriptionUnitsHelper steps 1-2). */
function resolveDailyQuantity(doseAmount, doseUnit, frequencyPerDay, drug, dispensableDoseUnits) {
  const unit = String(doseUnit || '').trim().toLowerCase()
  const isDispensable = dispensableDoseUnits.includes(unit)
  let unitsPerAdmin = doseAmount
  if (!isDispensable && MASS_UNITS.includes(unit)) {
    const strength = resolveDrugStrength(drug)
    if (strength.value == null || !strength.unit) {
      return { error: 'Mass DOSE_UNIT requires MasterDrugs STRENGTH / STRENGTH_VALUE' }
    }
    const doseMg = toMg(doseAmount, doseUnit)
    const strengthMg = toMg(strength.value, strength.unit)
    if (doseMg == null || strengthMg == null || strengthMg <= 0) {
      return { error: 'Unable to convert dose mass to drug strength units' }
    }
    unitsPerAdmin = doseMg / strengthMg
    return {
      dailyQty: unitsPerAdmin * frequencyPerDay,
      unitsPerAdmin,
      massConverted: true,
    }
  }
  return { dailyQty: unitsPerAdmin * frequencyPerDay, unitsPerAdmin, massConverted: false }
}

/** Box-count label for a mass dose: the pack's dispensable form, not mg/mcg/g. */
function massQuantityUnitLabel(drug) {
  const packageType = drug.PACKAGE_TYPES != null ? String(drug.PACKAGE_TYPES).trim() : ''
  return packageType ? formatDoseUnitLabel(packageType) : 'Tablet'
}

function buildDailyDoseCheck({ doseAmount, doseUnit, freq, dailyQty, drug }) {
  const strength = resolveDrugStrengthForDisplay(drug)
  const hasStrength = strength.value != null && !Number.isNaN(strength.value) && strength.value > 0 && !!strength.unit
  const unitLabel = hasStrength ? formatDoseUnitLabel(doseUnit) : resolveDispensableUnitLabel(doseUnit, drug)
  const strengthUnit = hasStrength ? String(strength.unit).trim() : ''
  const strengthUnitLower = strengthUnit.toLowerCase()

  let dailyDisplayValue
  let dailyDisplayUnit
  let desc
  if (hasStrength) {
    dailyDisplayValue = doseAmount * strength.value * freq
    dailyDisplayUnit = strengthUnitLower
    desc = `${fmt(doseAmount)} ${unitLabel} x ${fmt(strength.value)} ${strengthUnitLower} x ${fmt(freq)}/Day = ${fmt(dailyDisplayValue)} ${strengthUnitLower}/day`
  } else {
    dailyDisplayValue = doseAmount * freq
    dailyDisplayUnit = unitLabel.toLowerCase()
    desc = `${fmt(doseAmount)} ${unitLabel} x ${fmt(freq)}/Day = ${fmt(dailyDisplayValue)} ${dailyDisplayUnit}/day`
  }

  const hasMax = drug.MAX_DAILY_DOSE != null && drug.MAX_DAILY_DOSE !== ''
  const hasMin = drug.MIN_DAILY_DOSE != null && drug.MIN_DAILY_DOSE !== ''
  const maxDailyDose = hasMax ? toNum(drug.MAX_DAILY_DOSE) : null
  const minDailyDose = hasMin ? toNum(drug.MIN_DAILY_DOSE) : null

  let maxText = ''
  let maxTablets = null
  if (maxDailyDose != null) {
    maxTablets = hasStrength ? maxDailyDose / strength.value : maxDailyDose
    const tabletLabel = maxTablets === 1 ? unitLabel : pluralizeDoseUnitLabel(unitLabel)
    maxText = hasStrength
      ? `Maximum ${fmt(maxDailyDose)} ${strengthUnit}/Day (${fmt(maxTablets)} ${tabletLabel})`
      : `Maximum ${fmt(maxDailyDose)} ${tabletLabel}/Day`
  }

  let withinlimit = true
  let failMessage = null
  const compareValue = hasStrength ? dailyDisplayValue : dailyQty
  if (maxDailyDose != null && compareValue != null && compareValue > maxDailyDose) {
    withinlimit = false
    failMessage = `${fmt(dailyDisplayValue)} ${dailyDisplayUnit}/day exceeds the maximum.`
  } else if (minDailyDose != null && compareValue != null && compareValue < minDailyDose) {
    withinlimit = false
    failMessage = `${fmt(dailyDisplayValue)} ${dailyDisplayUnit}/day is below the minimum.`
  }

  return {
    unitLabel,
    check: {
      title: 'DAILY DOSE CHECK',
      desc,
      max: maxText,
      prescribed: `${fmt(dailyDisplayValue)} ${dailyDisplayUnit}/day`,
      maxAllowedDosage: maxDailyDose != null ? `${fmt(maxDailyDose)} ${hasStrength ? strengthUnitLower : dailyDisplayUnit}/day` : '',
      maxAllowedTabletsPerDay: maxTablets != null ? fmt(maxTablets) : '',
      withinlimit,
      displayText: withinlimit ? `${fmt(dailyDisplayValue)}${dailyDisplayUnit}/day - within the maximum` : failMessage,
    },
  }
}

function buildQuantityCheck({ unitsPerAdmin, freq, dailyQty, supplyDays, durationValue, durationUnit, requestedQty, packageSize, unitLabel }) {
  const quantityUnitsPerDay = dailyQty
  const maxTotalUnits = quantityUnitsPerDay * supplyDays
  const maxBoxes = packageSize != null && packageSize > 0 ? Math.ceil(maxTotalUnits / packageSize) : Math.ceil(maxTotalUnits)

  const durationText = durationValue != null && durationUnit
    ? `${fmt(durationValue)} ${String(durationUnit).trim()}`
    : `${fmt(supplyDays)} Days`
  const unitLabelPlural = pluralizeDoseUnitLabel(unitLabel)
  const omitUnitInQty = String(unitLabel || '').trim().toLowerCase() === 'each'
  const qtyUnitSuffix = !omitUnitInQty && unitLabelPlural ? ` ${unitLabelPlural}` : ''
  const desc = `${fmt(unitsPerAdmin)} x ${fmt(freq)}/Day x ${durationText} = ${fmt(maxTotalUnits)}${qtyUnitSuffix}.`
  const packSizeText = packageSize != null ? `Pack Size ${fmt(packageSize)}${qtyUnitSuffix}.` : ''

  if (requestedQty == null) return { maxBoxes, check: null }

  let withinlimit = true
  let failMessage = null
  if (requestedQty > maxBoxes) {
    withinlimit = false
    const boxLabel = requestedQty === 1 ? 'box' : 'boxes'
    failMessage = `${requestedQty} ${boxLabel} exceeds the maximum of ${maxBoxes} for a ${supplyDays}-day supply. Reduce the quantity or shorten the duration.`
  }

  return {
    maxBoxes,
    check: {
      title: 'QUANTITY CHECK',
      desc,
      max: packSizeText,
      prescribed: `${fmt(requestedQty)} ${requestedQty === 1 ? 'Box' : 'Boxes'}`,
      withinlimit,
      displayText: withinlimit ? `${fmt(requestedQty)} boxes - within the approved quantity.` : failMessage,
    },
  }
}

function buildDurationCheck({ supplyDays, maxDurationDays, categoryCode }) {
  let withinlimit = true
  let failMessage = null
  if (maxDurationDays != null && supplyDays > maxDurationDays) {
    withinlimit = false
    failMessage = `${fmt(supplyDays)} days exceeds the maximum of ${fmt(maxDurationDays)} days for category ${categoryCode}.`
  }
  return {
    title: 'DURATION CHECK',
    desc: `${fmt(supplyDays)} Days selected.`,
    max: maxDurationDays != null ? `Maximum ${fmt(maxDurationDays)} Days${categoryCode ? ` for category ${categoryCode}` : ''}.` : '',
    prescribed: `${fmt(supplyDays)} Days`,
    withinlimit,
    displayText: withinlimit ? `${fmt(supplyDays)} days - within the approved duration.` : failMessage,
  }
}

/**
 * @param {Object} form { dose, dose_unit, freq_id, dur, dur_unit, dur_unit_id, quantity, icd_code }
 * @param {Object} governance `governance` from /prescription/data
 * @returns {Object|null} null when governance is disabled; otherwise each check is null until its inputs are filled.
 */
export function computePrescriptionChecks(form, governance) {
  if (!governance || !governance.enabled || !form) return null
  const drug = governance.drug || {}

  const result = {
    dailyDoseCheck: null,
    quantityCheck: null,
    durationCheck: null,
    maxBoxes: null,
    calcError: null,
    failed: false,
    failMessage: '',
  }

  const doseAmount = toNum(form.dose)
  const doseUnit = form.dose_unit
  const freq = form.freq_id != null ? toNum((governance.frequencyPerDay || {})[form.freq_id]) : null
  const durationValue = toNum(form.dur)
  const factor = form.dur_unit_id != null ? toNum((governance.durationDaysFactor || {})[form.dur_unit_id]) : null
  // A selected unit with no master factor must not be treated as days.
  const supplyDays = durationValue != null && durationValue > 0 && form.dur_unit && factor != null && factor > 0
    ? durationValue * factor
    : null

  let unitLabel = null
  let unitsPerAdmin = null
  let dailyQty = null
  if (doseAmount != null && doseAmount > 0 && doseUnit && freq != null && freq > 0) {
    const calc = resolveDailyQuantity(doseAmount, doseUnit, freq, drug, governance.dispensableDoseUnits || [])
    if (calc.error) {
      result.calcError = calc.error
    } else {
      const daily = buildDailyDoseCheck({ doseAmount, doseUnit, freq, dailyQty: calc.dailyQty, drug })
      result.dailyDoseCheck = daily.check
      unitsPerAdmin = calc.unitsPerAdmin
      dailyQty = calc.dailyQty
      unitLabel = calc.massConverted ? massQuantityUnitLabel(drug) : daily.unitLabel
    }
  }

  if (unitLabel && dailyQty != null && supplyDays != null) {
    const qty = buildQuantityCheck({
      unitsPerAdmin,
      freq,
      dailyQty,
      supplyDays,
      durationValue,
      durationUnit: form.dur_unit,
      requestedQty: toNum(form.quantity),
      packageSize: toNum(drug.PACKAGE_SIZE != null ? drug.PACKAGE_SIZE : (drug.UNITS_PER_PACK != null ? drug.UNITS_PER_PACK : resolveUnitsPerPack(drug))),
      unitLabel,
    })
    result.maxBoxes = qty.maxBoxes
    result.quantityCheck = qty.check
  }

  if (supplyDays != null) {
    const categoryCode = form.icd_code ? (governance.icdCategories || {})[form.icd_code] || '' : ''
    const maxDurationDays = categoryCode ? toNum((governance.categoryMaxDuration || {})[categoryCode]) : null
    result.durationCheck = buildDurationCheck({ supplyDays, maxDurationDays, categoryCode })
  }

  const failures = [result.dailyDoseCheck, result.quantityCheck, result.durationCheck]
    .filter((c) => c && !c.withinlimit)
    .map((c) => c.displayText)
  if (result.calcError) failures.unshift(result.calcError)
  result.failed = failures.length > 0
  result.failMessage = failures.join(' & ')

  if (!result.dailyDoseCheck && !result.quantityCheck && !result.durationCheck && !result.calcError) return null
  return result
}
